import { forwardRef, KeyboardEvent, useImperativeHandle, useState } from "react";
import { ClickAwayListener, List, ListItemButton, ListItemText, Paper, Popper } from "@mui/material";
import aiChatSkillPopperStyles from "./aiChatSkillPopperStyles";

// Source of truth for slash skills is the Legalbird AI app (pdf_forms/definitions.py and the
// workflow skill markdown files); new skills must be mirrored here manually. Intentionally
// hardcoded: replace with an API call once we have more than 10 skills.
const AI_CHAT_SKILLS = [
  {
    command: "/vollstreckungsauftrag",
    description: "Vollstreckungsauftrag als PDF aus den Falldaten erstellen.",
  },
  {
    command: "/fristen",
    description: "Haupt- und Vorfristen als Aktivitäten am Fall notieren.",
  },
];

type AIChatSkillPopperProps = {
  anchorEl: HTMLElement | null;
  prompt: string;
  disabled: boolean;
  isDismissed: boolean;
  onDismiss: () => void;
  onSelect: (command: string) => void;
};

export type AIChatSkillPopperHandle = {
  handleKeyDown: (event: KeyboardEvent) => boolean;
};

const AIChatSkillPopper = forwardRef<AIChatSkillPopperHandle, AIChatSkillPopperProps>(function AIChatSkillPopper(
  { anchorEl, prompt, disabled, isDismissed, onDismiss, onSelect },
  ref,
) {
  const [selection, setSelection] = useState({ prompt: "", index: 0 });
  const matchingSkills =
    prompt.startsWith("/") && !/\s/.test(prompt)
      ? AI_CHAT_SKILLS.filter((skill) => skill.command.startsWith(prompt.toLowerCase()))
      : [];
  const isOpen = matchingSkills.length > 0 && !isDismissed && !disabled;
  const selectedIndex =
    matchingSkills.length > 0 && selection.prompt === prompt ? selection.index % matchingSkills.length : 0;

  // This improves usability for the popper: Arrow keys and enter selection
  const handleKeyDown = (event: KeyboardEvent) => {
    if (!isOpen) {
      return false;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setSelection({ prompt, index: (selectedIndex + step + matchingSkills.length) % matchingSkills.length });
      return true;
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      onSelect(matchingSkills[selectedIndex].command);
      return true;
    }
    return false;
  };
  useImperativeHandle(ref, () => ({ handleKeyDown }));

  return (
    <Popper open={isOpen} anchorEl={anchorEl} placement={"top-start"} sx={aiChatSkillPopperStyles.popper}>
      <ClickAwayListener onClickAway={onDismiss}>
        <Paper elevation={8} sx={aiChatSkillPopperStyles.menu}>
          <List dense disablePadding>
            {matchingSkills.map((skill, index) => (
              <ListItemButton
                key={skill.command}
                selected={index === selectedIndex}
                onClick={() => onSelect(skill.command)}
                sx={aiChatSkillPopperStyles.menuItem}
              >
                <ListItemText
                  primary={skill.command}
                  secondary={skill.description}
                  slotProps={{ primary: { variant: "body2" }, secondary: { variant: "caption" } }}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
});

export default AIChatSkillPopper;
